import React, { useState } from 'react';
import { Storage, Auth } from 'aws-amplify';
import ActivityTracker from '../utils/activityTracker';
import './ClientUploader.css';

const ClientUploader = ({ company }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    const validFiles = newFiles.filter(file => {
      if (allowedTypes.includes(file.type) || file.name.toLowerCase().endsWith('.ai')) {
        return true;
      }
      alert(`File type ${file.type} is not supported. Please upload images, PDFs, documents, or AI files.`);
      return false;
    });

    const filesWithMetadata = validFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: formatFileSize(file.size),
      type: file.type,
      status: 'pending'
    }));

    setFiles(prevFiles => [...prevFiles, ...filesWithMetadata]);
  };

  const removeFile = (fileId) => {
    setFiles(files.filter(f => f.id !== fileId));
    const newProgress = { ...uploadProgress };
    delete newProgress[fileId];
    setUploadProgress(newProgress);
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    setUploading(true);

    try {
      const currentUser = await Auth.currentAuthenticatedUser();

      for (const fileItem of files) {
        if (fileItem.status !== 'pending') continue;

        // Update status to uploading
        setFiles(prevFiles =>
          prevFiles.map(f =>
            f.id === fileItem.id ? { ...f, status: 'uploading' } : f
          )
        );

        try {
          // Upload to client's brand assets folder
          const key = `clients/${company.id}/brand-assets/${fileItem.file.name}`;

          await Storage.put(key, fileItem.file, {
            progressCallback: (progress) => {
              const percentage = Math.round((progress.loaded / progress.total) * 100);
              setUploadProgress(prev => ({
                ...prev,
                [fileItem.id]: percentage
              }));
            },
            contentType: fileItem.file.type || 'application/octet-stream',
            level: 'public',
            metadata: {
              uploadedBy: currentUser.username,
              uploadedAt: new Date().toISOString(),
              category: 'brand-assets'
            }
          });

          // Update status to completed
          setFiles(prevFiles =>
            prevFiles.map(f =>
              f.id === fileItem.id ? { ...f, status: 'completed' } : f
            )
          );

          // Track client upload activity
          const clientName = currentUser.attributes?.name || currentUser.username || 'Unknown User';
          ActivityTracker.trackClientFileUpload(
            fileItem.file.name,
            clientName,
            company.name,
            fileItem.file.size
          );

        } catch (error) {
          console.error('Error uploading file:', error);

          // Update status to error
          setFiles(prevFiles =>
            prevFiles.map(f =>
              f.id === fileItem.id ? { ...f, status: 'error' } : f
            )
          );
        }
      }
    } catch (error) {
      console.error('Upload process error:', error);
    } finally {
      setUploading(false);
    }
  };

  const clearCompleted = () => {
    setFiles(files.filter(f => f.status !== 'completed'));
    setUploadProgress({});
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    return '📁';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'uploading': return '📤';
      case 'completed': return '✅';
      case 'error': return '❌';
      default: return '📁';
    }
  };

  return (
    <div className="client-uploader">
      <div className="uploader-header">
        <h3>📤 Upload Brand Assets</h3>
        <p>Share your logos, brand guidelines, and other assets with 1209 Digital</p>
      </div>

      <div
        className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="upload-content">
          <div className="upload-icon">📁</div>
          <h4>Drag & Drop Files Here</h4>
          <p>or <label htmlFor="file-upload" className="upload-link">browse files</label></p>
          <input
            id="file-upload"
            type="file"
            multiple
            onChange={handleFileInput}
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.txt,.ai"
          />
          <div className="supported-formats">
            <small>Supported: Images, PDFs, Documents, AI files</small>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="upload-queue">
          <div className="queue-header">
            <h4>Upload Queue ({files.length} files)</h4>
            <div className="queue-actions">
              {files.some(f => f.status === 'completed') && (
                <button onClick={clearCompleted} className="clear-button">
                  Clear Completed
                </button>
              )}
              <button
                onClick={uploadFiles}
                disabled={uploading || files.every(f => f.status !== 'pending')}
                className="upload-button"
              >
                {uploading ? 'Uploading...' : 'Upload All'}
              </button>
            </div>
          </div>

          <div className="file-list">
            {files.map(file => (
              <div key={file.id} className={`upload-file-item ${file.status}`}>
                <div className="file-info">
                  <span className="file-icon">{getFileIcon(file.type)}</span>
                  <div className="file-details">
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">{file.size}</div>
                  </div>
                </div>

                <div className="file-progress">
                  {file.status === 'uploading' && (
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress[file.id] || 0}%` }}
                      />
                      <span className="progress-text">
                        {uploadProgress[file.id] || 0}%
                      </span>
                    </div>
                  )}
                  {file.status === 'completed' && (
                    <span className="status-text completed">✅ Uploaded</span>
                  )}
                  {file.status === 'error' && (
                    <span className="status-text error">❌ Failed</span>
                  )}
                  {file.status === 'pending' && (
                    <span className="status-text pending">⏳ Waiting</span>
                  )}
                </div>

                <div className="file-actions">
                  <span className="status-icon">{getStatusIcon(file.status)}</span>
                  {file.status !== 'uploading' && (
                    <button
                      onClick={() => removeFile(file.id)}
                      className="remove-button"
                      title="Remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientUploader;